import { cn } from "@/lib/utils";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";

/**
 * Audio waveform visualizer component
 * Uses Web Audio API to analyze audio stream and render frequency bars
 */
export function DictationVisualizer({
  audioStream,
  isRecording,
  className,
  barColor = "#a855f7", // Purple-500
  backgroundColor = "transparent",
  barWidth = 3,
  gap = 2,
  height = 32,
  width = 200,
}) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!audioStream || !isRecording) {
      // Clean up when not recording
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
        analyserRef.current = null;
        sourceRef.current = null;
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set up audio context and analyser
    const setupAudio = async () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();

        // Resume if suspended (browser autoplay policy)
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;

        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        sourceRef.current = source;

        // Start animation
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
          if (!isRecording || !analyserRef.current) {
            return;
          }

          animationRef.current = requestAnimationFrame(draw);

          analyserRef.current.getByteFrequencyData(dataArray);

          // Clear canvas
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Calculate number of bars that fit
          const totalBarWidth = barWidth + gap;
          const numBars = Math.floor(canvas.width / totalBarWidth);

          // Sample data evenly across the frequency spectrum
          const step = Math.floor(bufferLength / numBars);

          ctx.fillStyle = barColor;

          for (let i = 0; i < numBars; i++) {
            // Get average of nearby frequencies for smoother visualization
            let sum = 0;
            const startIdx = i * step;
            const endIdx = Math.min(startIdx + step, bufferLength);
            for (let j = startIdx; j < endIdx; j++) {
              sum += dataArray[j];
            }
            const value = sum / (endIdx - startIdx);

            // Normalize to canvas height with minimum height
            const barHeight = Math.max(2, (value / 255) * canvas.height);

            // Draw centered bar
            const x = i * totalBarWidth;
            const y = (canvas.height - barHeight) / 2;

            // Draw rounded bar
            ctx.beginPath();
            ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
            ctx.fill();
          }
        };

        draw();
      } catch (err) {
        console.error("Failed to set up audio visualizer:", err);
      }
    };

    setupAudio();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
        analyserRef.current = null;
        sourceRef.current = null;
      }
    };
  }, [audioStream, isRecording, barColor, backgroundColor, barWidth, gap]);

  // Handle canvas sizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set actual pixel dimensions (for sharp rendering)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    // Scale context to match
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      className={cn("block", className)}
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
    />
  );
}

DictationVisualizer.propTypes = {
  audioStream: PropTypes.object,
  isRecording: PropTypes.bool,
  className: PropTypes.string,
  barColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  barWidth: PropTypes.number,
  gap: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
};

export default DictationVisualizer;
